import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PageContainer, PageHeader } from '@/components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader2, AlertCircle, CheckCircle2, FileText, Receipt, Building2, BanIcon,
  ShieldCheck, ArrowLeft, Info,
} from 'lucide-react';
import {
  receiptInfoApi, ReceiptInfo, ReceiptType, CashReceiptPurpose,
} from '@/api/planApi';

// ── 헬퍼 ─────────────────────────────────────────────────────

function formatPhoneNumber(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
}

function formatBizNo(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

// ── 타입 ─────────────────────────────────────────────────────

interface FormState {
  receiptType: ReceiptType;
  cashReceiptPurpose: CashReceiptPurpose;
  phoneNumber: string;
  cashReceiptBizNo: string;
  taxInvoiceBizNo: string;
  companyName: string;
  ceoName: string;
  invoiceEmail: string;
}

const EMPTY_FORM: FormState = {
  receiptType: 'NONE',
  cashReceiptPurpose: 'PERSONAL_DEDUCTION',
  phoneNumber: '',
  cashReceiptBizNo: '',
  taxInvoiceBizNo: '',
  companyName: '',
  ceoName: '',
  invoiceEmail: '',
};

// ── 서브컴포넌트: 유형 선택 카드 ──────────────────────────────

interface TypeCardProps {
  selected: boolean;
  onClick: () => void;
  icon: React.ElementType;
  label: string;
  sub?: string;
}

function TypeCard({ selected, onClick, icon: Icon, label, sub }: TypeCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 py-4 px-3 text-sm font-medium transition-all ${
        selected
          ? 'border-primary bg-primary/5 text-primary'
          : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      <Icon className={`w-5 h-5 ${selected ? 'text-primary' : 'text-gray-400'}`} />
      <span>{label}</span>
      {sub && <span className="text-[11px] text-gray-400 font-normal">{sub}</span>}
    </button>
  );
}

// ── 서브컴포넌트: 용도 선택 라디오 ───────────────────────────

interface PurposeOptionProps {
  selected: boolean;
  onClick: () => void;
  label: string;
  sub: string;
}

function PurposeOption({ selected, onClick, label, sub }: PurposeOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-3 rounded-lg border-2 py-3 px-4 text-left text-sm transition-all ${
        selected
          ? 'border-primary bg-primary/5 text-primary'
          : 'border-gray-200 text-gray-600 hover:border-gray-300'
      }`}
    >
      <span
        className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
          selected ? 'border-primary' : 'border-gray-300'
        }`}
      >
        {selected && <span className="w-2 h-2 rounded-full bg-primary" />}
      </span>
      <div>
        <div className="font-medium">{label}</div>
        <div className="text-xs text-gray-400 font-normal">{sub}</div>
      </div>
    </button>
  );
}

// ── 메인 컴포넌트 ─────────────────────────────────────────────

export default function ReceiptInfoPage() {
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [bizValidated, setBizValidated] = useState(false);
  const [bizStatusLoading, setBizStatusLoading] = useState(false);
  const [bizStatusWarning, setBizStatusWarning] = useState<string | null>(null);
  const [bizStatusSuccess, setBizStatusSuccess] = useState<string | null>(null);
  const [hasExisting, setHasExisting] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  useEffect(() => {
    loadReceiptInfo();
  }, []);

  const loadReceiptInfo = async () => {
    setPageLoading(true);
    setError(null);
    try {
      const res = await receiptInfoApi.get();
      const info = res.data;
      if (!info || !info.receiptType) {
        // 미설정
        return;
      }
      setHasExisting(true);
      const isCashPersonal = info.receiptType === 'CASH_RECEIPT' && info.cashReceiptPurpose === 'PERSONAL_DEDUCTION';
      const isCashBusiness = info.receiptType === 'CASH_RECEIPT' && info.cashReceiptPurpose === 'BUSINESS_EXPENSE';
      setForm({
        receiptType: info.receiptType ?? 'NONE',
        cashReceiptPurpose: info.cashReceiptPurpose ?? 'PERSONAL_DEDUCTION',
        phoneNumber: isCashPersonal ? (info.cashReceiptNumberMasked ?? '') : '',
        cashReceiptBizNo: isCashBusiness ? (info.cashReceiptNumberMasked ?? '') : '',
        taxInvoiceBizNo: info.bizRegNoMasked ?? '',
        companyName: info.bizName ?? '',
        ceoName: info.bizCeoName ?? '',
        invoiceEmail: info.taxInvoiceEmail ?? '',
      });
      if (info.bizName || info.bizCeoName) setBizValidated(true);
    } catch (err: any) {
      // 404: 아직 설정 없음 (정상)
      if (err?.response?.status !== 404) {
        setError('증빙 설정을 불러오지 못했습니다.');
      }
    } finally {
      setPageLoading(false);
    }
  };

  const handleBizStatusCheck = async (rawBizNo: string) => {
    setBizStatusLoading(true);
    setBizStatusWarning(null);
    setBizStatusSuccess(null);
    try {
      const res = await receiptInfoApi.checkBusinessStatus(rawBizNo);
      const data = res.data;
      if (data.found) {
        if (data.bizStatusCode === '03') {
          setBizStatusWarning('본 사업자는 폐업 상태이므로 세금계산서 발행이 제한됩니다. 관련 문의는 담당자(alt.generation@outlook.com)로 연락 부탁드립니다.');
        } else if (data.bizStatusCode === '02') {
          setBizStatusWarning(`${data.bizStatus ?? '휴업'} 사업자입니다. 세금계산서 발행이 불가할 수 있습니다.`);
        } else if (data.bizStatusCode === '01') {
          setBizStatusSuccess('사업자번호가 확인되었습니다. (계속사업자)');
        }
      }
    } catch {
      // 상태조회 실패는 무시 (진위확인에서 최종 검증)
    } finally {
      setBizStatusLoading(false);
    }
  };

  const handleBizValidate = async () => {
    const rawBizNo = form.taxInvoiceBizNo.replace(/\D/g, '');
    if (rawBizNo.length !== 10) {
      setLookupError('사업자등록번호 10자리를 입력하세요.');
      return;
    }
    if (!form.companyName.trim()) {
      setLookupError('상호명을 입력하세요.');
      return;
    }
    if (!form.ceoName.trim()) {
      setLookupError('대표자명을 입력하세요.');
      return;
    }
    setValidating(true);
    setLookupError(null);
    try {
      const res = await receiptInfoApi.validateBusiness(form.taxInvoiceBizNo, form.companyName.trim(), form.ceoName.trim());
      const data = res.data;
      if (!data.valid) {
        setLookupError(data.message ?? '사업자 정보가 일치하지 않습니다. 사업자번호·상호명·대표자명을 다시 확인해 주세요.');
        setBizValidated(false);
        return;
      }
      setBizValidated(true);
    } catch (err: any) {
      setLookupError(err?.response?.data?.message ?? '진위확인 중 오류가 발생했습니다.');
      setBizValidated(false);
    } finally {
      setValidating(false);
    }
  };

  const isMasked = (v: string) => v.includes('*');

  const validate = (): string | null => {
    const { receiptType, cashReceiptPurpose, phoneNumber, cashReceiptBizNo, taxInvoiceBizNo, invoiceEmail } = form;
    if (receiptType === 'CASH_RECEIPT') {
      if (cashReceiptPurpose === 'PERSONAL_DEDUCTION') {
        if (!isMasked(phoneNumber)) {
          const d = phoneNumber.replace(/\D/g, '');
          if (!d) return '휴대폰번호를 입력하세요.';
          if (d.length < 10 || d.length > 11) return '유효한 휴대폰번호를 입력하세요.';
          if (!d.startsWith('01')) return '휴대폰번호는 01로 시작해야 합니다.';
        }
      } else {
        if (!isMasked(cashReceiptBizNo)) {
          const d = cashReceiptBizNo.replace(/\D/g, '');
          if (!d) return '사업자등록번호를 입력하세요.';
          if (d.length !== 10) return '사업자등록번호 10자리를 입력하세요.';
        }
      }
    } else if (receiptType === 'TAX_INVOICE') {
      // 마스킹된 값이 있는 경우 모든 검증 건너뜀 (기존 설정 유지)
      if (isMasked(taxInvoiceBizNo)) {
        return null;
      }
      const d = taxInvoiceBizNo.replace(/\D/g, '');
      if (!d) return '사업자등록번호를 입력하세요.';
      if (d.length !== 10) return '사업자등록번호 10자리를 입력하세요.';
      if (!form.companyName.trim()) return '상호명을 입력하세요.';
      if (!form.ceoName.trim()) return '대표자명을 입력하세요.';
      if (!bizValidated) return '사업자 진위확인을 완료해 주세요.';
      if (!invoiceEmail.trim()) return '세금계산서 수신 이메일을 입력하세요.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invoiceEmail.trim())) return '유효한 이메일 주소를 입력하세요.';
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSaving(true);
    setError(null);
    setSaveSuccess(false);
    try {
      const isCash = form.receiptType === 'CASH_RECEIPT';
      const isTax = form.receiptType === 'TAX_INVOICE';
      // 마스킹된 값(*포함)은 변경하지 않은 것이므로 null로 전송 (B/E가 기존값 유지)
      const payload: ReceiptInfo = {
        receiptType: form.receiptType,
        cashReceiptPurpose: isCash ? form.cashReceiptPurpose : null,
        phoneNumber: isCash && form.cashReceiptPurpose === 'PERSONAL_DEDUCTION' && !isMasked(form.phoneNumber)
          ? form.phoneNumber.replace(/\D/g, '') : null,
        cashReceiptBizNo: isCash && form.cashReceiptPurpose === 'BUSINESS_EXPENSE' && !isMasked(form.cashReceiptBizNo)
          ? form.cashReceiptBizNo.replace(/\D/g, '') : null,
        taxInvoiceBizNo: isTax && !isMasked(form.taxInvoiceBizNo) ? form.taxInvoiceBizNo.replace(/\D/g, '') : null,
        companyName: isTax ? form.companyName : null,
        ceoName: isTax ? form.ceoName : null,
        invoiceEmail: isTax ? form.invoiceEmail.trim() : null,
      };
      await receiptInfoApi.save(payload);
      setHasExisting(true);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 5000);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? '저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="flex justify-center items-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <PageContainer maxWidth="full">
      <div className="flex items-start gap-3">
        <Link to="/dashboard/profile/edit" className="mt-1 text-gray-400 hover:text-gray-600">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <PageHeader 
            title="증빙 신청 설정" 
            description="입금 확인 시 자동 발행할 증빙 유형을 설정합니다."
          />
        </div>
      </div>

      {/* 에러 / 성공 알림 */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {saveSuccess && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle2 className="w-4 h-4 text-green-600" />
          <AlertDescription className="text-green-700">증빙 설정이 저장되었습니다.</AlertDescription>
        </Alert>
      )}

      {/* 저장된 설정 안내 */}
      {hasExisting && !saveSuccess && (
        <Alert className="border-blue-200 bg-blue-50">
          <Info className="w-4 h-4 text-blue-600" />
          <AlertDescription className="text-blue-700 text-sm">
            저장된 설정입니다. 수정 후 저장하면 다음 입금 확인부터 반영됩니다.
          </AlertDescription>
        </Alert>
      )}

      {/* 폼 카드 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">증빙 유형 선택</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* 유형 선택 카드 3개 */}
          <div className="grid grid-cols-3 gap-3">
            <TypeCard
              selected={form.receiptType === 'CASH_RECEIPT'}
              onClick={() => { setForm(prev => ({ ...prev, receiptType: 'CASH_RECEIPT' })); setError(null); }}
              icon={Receipt}
              label="현금영수증"
              sub="개인 · 사업자"
            />
            <TypeCard
              selected={form.receiptType === 'TAX_INVOICE'}
              onClick={() => { setForm(prev => ({ ...prev, receiptType: 'TAX_INVOICE' })); setError(null); }}
              icon={Building2}
              label="세금계산서"
              sub="사업자 전용"
            />
            <TypeCard
              selected={form.receiptType === 'NONE'}
              onClick={() => { setForm(prev => ({ ...prev, receiptType: 'NONE' })); setError(null); }}
              icon={BanIcon}
              label="발행 안함"
            />
          </div>

          {/* ── 현금영수증 ── */}
          {form.receiptType === 'CASH_RECEIPT' && (
            <div className="space-y-4 pt-2 border-t">
              <p className="text-sm font-medium text-gray-700">용도 선택</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <PurposeOption
                  selected={form.cashReceiptPurpose === 'PERSONAL_DEDUCTION'}
                  onClick={() => setForm(prev => ({ ...prev, cashReceiptPurpose: 'PERSONAL_DEDUCTION' }))}
                  label="개인 소득공제용"
                  sub="휴대폰번호 입력"
                />
                <PurposeOption
                  selected={form.cashReceiptPurpose === 'BUSINESS_EXPENSE'}
                  onClick={() => setForm(prev => ({ ...prev, cashReceiptPurpose: 'BUSINESS_EXPENSE' }))}
                  label="사업자 지출증빙용"
                  sub="사업자번호 입력"
                />
              </div>

              {form.cashReceiptPurpose === 'PERSONAL_DEDUCTION' && (
                <div className="space-y-1.5">
                  <Label htmlFor="phoneNumber">
                    휴대폰번호 <span className="text-red-500">*</span>
                  </Label>
                  {isMasked(form.phoneNumber) ? (
                    <div className="flex items-center gap-2">
                      <Input
                        id="phoneNumber"
                        type="tel"
                        value={form.phoneNumber}
                        disabled
                        className="bg-gray-50"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setForm(prev => ({ ...prev, phoneNumber: '' }))}
                      >
                        변경
                      </Button>
                    </div>
                  ) : (
                    <Input
                      id="phoneNumber"
                      type="tel"
                      placeholder="010-0000-0000"
                      value={form.phoneNumber}
                      onChange={e => setForm(prev => ({
                        ...prev, phoneNumber: formatPhoneNumber(e.target.value),
                      }))}
                      maxLength={13}
                    />
                  )}
                  <p className="text-xs text-gray-400">
                    국세청에 등록된 휴대폰번호로 소득공제가 처리됩니다.
                  </p>
                </div>
              )}

              {form.cashReceiptPurpose === 'BUSINESS_EXPENSE' && (
                <div className="space-y-1.5">
                  <Label htmlFor="cashReceiptBizNo">
                    사업자등록번호 <span className="text-red-500">*</span>
                  </Label>
                  {isMasked(form.cashReceiptBizNo) ? (
                    <div className="flex items-center gap-2">
                      <Input
                        id="cashReceiptBizNo"
                        value={form.cashReceiptBizNo}
                        disabled
                        className="bg-gray-50"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setForm(prev => ({ ...prev, cashReceiptBizNo: '' }))}
                      >
                        변경
                      </Button>
                    </div>
                  ) : (
                    <Input
                      id="cashReceiptBizNo"
                      placeholder="000-00-00000"
                      value={form.cashReceiptBizNo}
                      onChange={e => setForm(prev => ({
                        ...prev, cashReceiptBizNo: formatBizNo(e.target.value),
                      }))}
                      maxLength={12}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── 세금계산서 ── */}
          {form.receiptType === 'TAX_INVOICE' && (
            <div className="space-y-4 pt-2 border-t">
              <div className="space-y-1.5">
                <Label htmlFor="taxInvoiceBizNo">
                  사업자등록번호 <span className="text-red-500">*</span>
                </Label>
                {isMasked(form.taxInvoiceBizNo) ? (
                  <div className="flex items-center gap-2">
                    <Input
                      id="taxInvoiceBizNo"
                      value={form.taxInvoiceBizNo}
                      disabled
                      className="bg-gray-50"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setForm(prev => ({ ...prev, taxInvoiceBizNo: '' }));
                        setBizValidated(false);
                        setBizStatusWarning(null);
                        setBizStatusSuccess(null);
                        setLookupError(null);
                      }}
                    >
                      변경
                    </Button>
                  </div>
                ) : (
                  <Input
                    id="taxInvoiceBizNo"
                    placeholder="000-00-00000"
                    value={form.taxInvoiceBizNo}
                    onChange={e => {
                      const formatted = formatBizNo(e.target.value);
                      setForm(prev => ({ ...prev, taxInvoiceBizNo: formatted }));
                      setBizValidated(false);
                      setBizStatusWarning(null);
                      setBizStatusSuccess(null);
                      setLookupError(null);
                      const raw = formatted.replace(/\D/g, '');
                      if (raw.length === 10) handleBizStatusCheck(raw);
                    }}
                    maxLength={12}
                  />
                )}
                {bizStatusLoading && (
                  <p className="text-xs text-gray-400 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    사업자 상태 조회 중...
                  </p>
                )}
                {bizStatusWarning && (
                  <Alert className="border-orange-300 bg-orange-50 py-2">
                    <AlertCircle className="w-3.5 h-3.5 text-orange-500" />
                    <AlertDescription className="text-orange-700 text-xs">{bizStatusWarning}</AlertDescription>
                  </Alert>
                )}
                {bizStatusSuccess && (
                  <div className="space-y-1.5">
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      {bizStatusSuccess}
                    </p>
                    <Alert className="border-blue-200 bg-blue-50 py-2">
                      <AlertCircle className="w-3.5 h-3.5 text-blue-500" />
                      <AlertDescription className="text-blue-700 text-xs">
                        입력하신 상호명과 대표자명이 사업자등록증과 일치하는지 반드시 확인해 주세요. 잘못된 정보로 발행된 세금계산서는 수정이 어려울 수 있습니다.
                      </AlertDescription>
                    </Alert>
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="companyName">
                  상호명 <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="companyName"
                  placeholder="상호명을 입력하세요"
                  value={form.companyName}
                  onChange={e => {
                    setForm(prev => ({ ...prev, companyName: e.target.value }));
                    setBizValidated(false);
                    setLookupError(null);
                  }}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ceoName">
                  대표자명 <span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="ceoName"
                    placeholder="대표자명을 입력하세요"
                    value={form.ceoName}
                    onChange={e => {
                      setForm(prev => ({ ...prev, ceoName: e.target.value }));
                      setBizValidated(false);
                      setLookupError(null);
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-shrink-0"
                    disabled={validating}
                    onClick={handleBizValidate}
                  >
                    {validating
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <ShieldCheck className="w-4 h-4" />}
                    <span className="ml-1.5">진위확인</span>
                  </Button>
                </div>
                {lookupError && (
                  <p className="text-xs text-red-500">{lookupError}</p>
                )}
                {bizValidated && (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    사업자 정보 진위확인이 완료되었습니다.
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="invoiceEmail">
                  세금계산서 수신 이메일 <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="invoiceEmail"
                  type="email"
                  placeholder="email@example.com"
                  value={form.invoiceEmail}
                  onChange={e => setForm(prev => ({ ...prev, invoiceEmail: e.target.value }))}
                />
                <p className="text-xs text-gray-400">국세청 전자세금계산서가 이메일로 발송됩니다.</p>
              </div>

              <Alert className="border-orange-200 bg-orange-50">
                <AlertCircle className="w-4 h-4 text-orange-500" />
                <AlertDescription className="text-orange-700 text-xs">
                  세금계산서는 사업자만 신청 가능합니다. 사업자번호·상호명·대표자명 입력 후 진위확인을 완료해 주세요.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* ── 발행 안함 ── */}
          {form.receiptType === 'NONE' && (
            <div className="pt-2 border-t">
              <Alert className="border-gray-200 bg-gray-50">
                <AlertDescription className="text-gray-600 text-sm">
                  증빙이 발행되지 않습니다. 나중에 언제든지 설정을 변경할 수 있습니다.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* 저장 버튼 */}
          <div className="pt-2 border-t flex justify-end">
            <Button onClick={handleSave} disabled={saving} className="min-w-[80px]">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
              저장
            </Button>
          </div>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
